import type { UserPresenceProps } from './types';
import cx from 'classnames';
import { STATE_PRESENCE } from 'Constants/enums';
import { MobXProviderContext, observer } from 'mobx-react';
import * as React from 'react';
import { RootStoreProps } from 'Stores/RootStore.types';
import { Styled } from './index.styles';

const testid = 'userPresence';

const COMPONENT_BY_STATUS: Record<STATE_PRESENCE, React.ReactElement> = {
  Active: <Styled.AvailableIcon icon="available-filled" size="tiny" />,
  Idle: <Styled.AwayIcon icon="away-filled" size="tiny" />,
  Away: <Styled.AwayIcon icon="away-filled" size="tiny" />,
  OnCall: <Styled.BusyIcon icon="busy-filled" size="tiny" />,
  Invisible: <Styled.OfflineIcon icon="offline-filled" size="tiny" />,
  DoNotDisturb: <Styled.DNDIcon icon="do-not-disturb-filled" size="tiny" />,
  OffLine: <Styled.OfflineIcon icon="offline-filled" size="tiny" />,
  Unknown: <Styled.UnknownIcon icon="unknown-status" size="tiny" />,
};

const createPresenceIcon = (
  icon: React.ReactElement,
  unreadMessages: number
) => {
  const unreadCount = unreadMessages > 99 ? '99+' : unreadMessages;

  if (unreadMessages > 0) {
    return (
      <Styled.UserPresence data-testid={`${testid}-icon`}>
        <Styled.UnreadMessageCount
          className={cx('number-circle', {
            'over-99': unreadMessages > 99,
          })}
        >
          {unreadCount}
        </Styled.UnreadMessageCount>
        {icon}
      </Styled.UserPresence>
    );
  }

  return icon;
};

export const UserPresence: React.FC<UserPresenceProps> = observer(
  ({ state, conversationId, showUnread, className }) => {
    const {
      ui: { selectConversationUnreadCounts, conversationUnreadCounts },
    } = React.useContext<RootStoreProps>(MobXProviderContext);

    const unreadCounts = React.useMemo(
      () => selectConversationUnreadCounts(conversationId),
      [
        conversationId,
        selectConversationUnreadCounts,
        // eslint-disable-next-line react-hooks/exhaustive-deps
        conversationUnreadCounts.get(conversationId),
      ]
    );

    if (!state) return null;

    const unreadMessages = showUnread ? unreadCounts.unreadMessages : 0;

    return createPresenceIcon(
      React.cloneElement(COMPONENT_BY_STATUS[state || 'OffLine'], {
        className,
      }),
      unreadMessages
    );
  }
);
